import { useEffect, useRef } from 'react';
import { Container } from 'react-scale/components/pages/zeitgeist/layout/container';
import { gsap } from 'react-scale/lib/gsap';
import { useInView } from 'react-intersection-observer';
import { useMedia } from 'react-use';
import cn from 'classnames';

import s from './stats.module.scss';
import { StaggeredText } from '../../primitives/staggered-text';
import { useLantern } from 'react-scale/hooks/use-lantern';
import { useDeviceDetect } from 'react-scale/hooks/use-device-detect';

export const Stats = () => {
  const { isSafari } = useDeviceDetect();
  const isDesktop = useMedia('(min-width: 1024px)');
  const statsRef = useRef<HTMLParagraphElement[]>([]);
  const firstHrRef = useRef<HTMLHRElement>();
  const secondHrRef = useRef<HTMLHRElement>();
  const thirdHrRef = useRef<HTMLHRElement>();
  const { elementRef: firstLanternElementRef, maskRef: firstMaskRef } =
    useLantern<HTMLInputElement>({ size: 'md' });
  const { elementRef: secondLanternElementRef, maskRef: secondMaskRef } =
    useLantern<HTMLInputElement>({ size: 'md' });
  const { elementRef: thirdLanternElementRef, maskRef: thirdMaskRef } =
    useLantern<HTMLInputElement>({ size: 'md' });
  const { ref: firstDividerRef, inView: firstDividerInView } = useInView({
    threshold: 1,
    delay: 0.9,
    triggerOnce: true
  });
  const { ref: secondDividerRef, inView: secondDividerInView } = useInView({
    threshold: 1,
    delay: 0.9,
    triggerOnce: true
  });
  const { ref: thirdDividerRef, inView: thirdDividerInView } = useInView({
    threshold: 1,
    delay: 0.9,
    triggerOnce: true
  });

  useEffect(() => {
    if (!firstHrRef.current || !firstDividerInView) return;

    const tl = gsap.timeline({ defaults: { ease: 'Power3.easeOut' } });

    tl.to(firstHrRef.current, { scaleX: 1, duration: 1.5 });
    tl.fromTo(
      statsRef.current[0],
      {
        innerText: 0
      },
      {
        innerText: statsRef.current[0].innerHTML,
        duration: 1.5,
        snap: { innerText: 1 },
        onComplete: () => {
          if (isDesktop) firstMaskRef.current.style.display = 'inline-block';
        }
      },
      '<'
    );

    return () => {
      tl.kill();
    };
  }, [firstDividerInView, isDesktop]);

  useEffect(() => {
    if (!secondHrRef.current || !secondDividerInView) return;

    const tl = gsap.timeline({ defaults: { ease: 'Power3.easeOut' } });

    tl.to(secondHrRef.current, { scaleX: 1, duration: 1.5 });
    tl.fromTo(
      statsRef.current[1],
      {
        innerText: 0
      },
      {
        innerText: statsRef.current[1].innerHTML,
        duration: 1.5,
        snap: { innerText: 1 },
        onComplete: () => {
          if (isDesktop) secondMaskRef.current.style.display = 'inline-block';
        }
      },
      '<'
    );

    return () => {
      tl.kill();
    };
  }, [secondDividerInView, isDesktop]);

  useEffect(() => {
    if (!thirdHrRef.current || !thirdDividerInView) return;

    const tl = gsap.timeline({ defaults: { ease: 'Power3.easeOut' } });

    tl.to(thirdHrRef.current, { scaleX: 1, duration: 1.5 });
    tl.fromTo(
      statsRef.current[2],
      {
        innerText: 0
      },
      {
        innerText: statsRef.current[2].innerHTML,
        duration: 1.5,
        snap: { innerText: 1 },
        onComplete: () => {
          if (isDesktop) thirdMaskRef.current.style.display = 'inline-block';
        }
      },
      '<'
    );

    return () => {
      tl.kill();
    };
  }, [thirdDividerInView, isDesktop]);

  return (
    <Container size="sm">
      <div className={s.section}>
        <StaggeredText as="h6" className={cn(s['section__title'], 'hidden lg:block')}>
          Learn how the world's most ambitious companies are improving their business by
          adopting&nbsp;AI
        </StaggeredText>
        <h6 className={cn(s['section__title'], 'lg:hidden')}>
          Learn how the world's most ambitious companies are improving their business by
          adopting&nbsp;AI
        </h6>

        <div className={s['section__content']}>
          <div className={s.grid}>
            <StaggeredText as="p" className={s.paragraph} delay={0.1}>
              Companies that have adopted AI benefit from an improved ability to develop new
              products and services and improve existing products, better customer experiences, and
              improved collaboration across business&nbsp;functions.
            </StaggeredText>

            <p className={s['paragraph--mobile']}>
              Companies that have adopted AI benefit from an improved ability to develop new
              products and services and improve existing products, better customer experiences, and
              improved collaboration across business&nbsp;functions.
            </p>
          </div>
          <StaggeredText as="p" className={s['stat__title']}>
            New products or services
          </StaggeredText>
          <hr ref={firstHrRef} className={s['stat__divider']} />
          <div className={s['stat__body']} ref={firstDividerRef}>
            <div ref={firstLanternElementRef} className="relative">
              <p className={s['stat__number']} ref={ref => statsRef.current.push(ref)}>
                {firstDividerInView ? '89%' : '0%'}
              </p>
              {!isSafari && (
                <p className={s['stat__number--mask']} ref={firstMaskRef}>
                  89%
                </p>
              )}
            </div>
            <p className={s['stat__disclaimer']}>
              of companies adopting AI benefit from the ability to develop new products or services.
            </p>
          </div>
          <StaggeredText as="p" className={s['stat__title']}>
            Enhanced Customer Experience
          </StaggeredText>
          <hr ref={secondHrRef} className={s['stat__divider']} />
          <div className={s['stat__body']} ref={secondDividerRef}>
            <div ref={secondLanternElementRef} className="relative">
              <p className={s['stat__number']} ref={ref => statsRef.current.push(ref)}>
                {secondDividerInView ? '67%' : '0%'}
              </p>
              {!isSafari && (
                <p className={s['stat__number--mask']} ref={secondMaskRef}>
                  67%
                </p>
              )}
            </div>
            <p className={s['stat__disclaimer']}>
              of companies benefit from enhanced customer experiences.
            </p>
          </div>
          <StaggeredText as="p" className={s['stat__title']}>
            Improved Collaboration
          </StaggeredText>
          <hr ref={thirdHrRef} className={s['stat__divider']} />
          <div className={s['stat__body']} ref={thirdDividerRef}>
            <div ref={thirdLanternElementRef} className="relative">
              <p className={s['stat__number']} ref={ref => statsRef.current.push(ref)}>
                {thirdDividerInView ? '66%' : '0%'}
              </p>
              {!isSafari && (
                <p className={s['stat__number--mask']} ref={thirdMaskRef}>
                  66%
                </p>
              )}
            </div>
            <p className={s['stat__disclaimer']}>
              of companies reported improved collaboration across business functions.
            </p>
          </div>
        </div>
      </div>
    </Container>
  );
};
